import _ from 'lodash';
import {
    InscriptionT,
    getBudgetProposalFromInscription,
} from 'routes/BudgetModule/shared/entities/inscription';
import {
    GESTIONNAIRE_FIELD,
    ORG_CHART_FINANCE_FIELD,
    AGG_STATUS,
    OPERATION_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';
import { getAggStatus } from 'routes/BudgetModule/helpers/statusHelpers';
import {
    BudgetProposalT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import { ColDef } from 'ag-grid-community';
import { statusFormatter } from 'ui/AgGridReact/formatters';
import { BudgetBys, Phase } from 'business/constants';
import { getStatusField } from 'routes/BudgetModule/shared/phases/phase';

const doesInscriptionBelongToOperation = (
    inscription: InscriptionT,
    operationSoftwareId: string,
): boolean => inscription[OPERATION_SOFTWARE_ID] === operationSoftwareId;

// The goal of this helper is to check if the operation has inscription in the phase.
const isPhaseFieldInInscription = (
    inscription: InscriptionT,
    statusField: string,
    // @ts-expect-error [TS migration] Was not detected by flow
): boolean => !!inscription[statusField];

const getResourceOfInscription = (
    inscription: InscriptionT,
    by: BudgetBys,
): string => {
    if (by === GESTIONNAIRE_FIELD) {
        return _(inscription).get(GESTIONNAIRE_FIELD) || '';
    } else if (by === ORG_CHART_FINANCE_FIELD) {
        return _(inscription).get(ORG_CHART_FINANCE_FIELD) || '';
    }

    return '';
};

// TODO: Helper complexe qui nous contraint à fetch/manipuler TOUTES les inscriptions uniquement pour retrouver le status d'un BP.
// Un endpoint dédié pourrait plutôt récupérer les BPs et leur status côté back. (Shortcut ID: 28000)
const getAggStatusOperation = (
    resource: string,
    budgetProposal: BudgetProposalT,
    inscriptions: InscriptionT[],
    statusesSettings: StatusesSettingsT,
    phasesSettings: Phase[],
    operationSoftwareId: string,
    budgetProposals: BudgetProposalT[],
    by: BudgetBys,
): null | string => {
    if (!inscriptions) return null;
    const { phase } = budgetProposal;
    const statusField = getStatusField(phasesSettings, phase);

    // We filter inscriptions on software id, on the presence of the phase field, on the gestionnaire and finally on the bp
    const filteredInscription = inscriptions.filter(
        (inscription) =>
            doesInscriptionBelongToOperation(
                inscription,
                operationSoftwareId,
            ) &&
            isPhaseFieldInInscription(inscription, statusField) &&
            resource === getResourceOfInscription(inscription, by) &&
            _.isEqual(
                getBudgetProposalFromInscription(inscription, budgetProposals),
                budgetProposal,
            ),
    );
    return getAggStatus(
        filteredInscription,
        phase,
        statusesSettings,
        phasesSettings,
        true,
        // Specific for ampmetropole, due to their logic about "mother" and "daugther" segment operations
        // we consider that all "operations" displayed in this view are budgetable
        true,
    );
};

/** This Function is used to retrieve the Columns inside the operation view */
export const getDetailColumns = (
    operationSoftwareId: string,
    budgetProposals: BudgetProposalT[],
    statusesSettings: StatusesSettingsT,
    phasesSettings: Phase[],
    t: TranslationT,
): ColDef[] => {
    const cellStyleSemiBold = { fontWeight: 510 };

    return [
        {
            field: 'resource',
            headerName: t('budgetOverview.budgetProposalsByGestionnaire'),
            cellStyle: cellStyleSemiBold,
            maxWidth: 650,
            valueGetter: ({ data }) =>
                `${data.resource.resourceName} - ${data.budgetProposal?.name}`,
        },
        {
            field: 'phase',
            headerName: t('common.phase'),
            maxWidth: 250,
            cellRenderer: 'phaseCellRenderer',
            valueGetter: ({ data }) => data.budgetProposal?.phase,
        },
        {
            field: AGG_STATUS,
            headerName: t('common.status'),
            cellRenderer: 'aggStatusCellRenderer',
            maxWidth: 125,
            valueFormatter: statusFormatter,
            valueGetter: (params) =>
                params.data.budgetProposal &&
                getAggStatusOperation(
                    params.data.resource.resourceName,
                    params.data.budgetProposal,
                    params.context,
                    statusesSettings,
                    phasesSettings,
                    operationSoftwareId,
                    budgetProposals,
                    budgetProposals[0].by,
                ),
        },
    ];
};

export const getDraftOperationDetailColumns = ({
    t,
}: {
    t: TranslationT;
}): ColDef[] => {
    const cellStyleSemiBold = { fontWeight: 510 };

    const columns = [
        {
            field: 'resource',
            headerName: t('budgetOverview.budgetProposalsByGestionnaire'),
            cellStyle: cellStyleSemiBold,
            maxWidth: 650,

            valueGetter: ({
                data,
            }: {
                data: {
                    gestionnaireName?: string;
                    orgChartFinanceName?: string;
                    budgetProposalName: string;
                };
            }) =>
                `${data.gestionnaireName || data.orgChartFinanceName} - ${
                    data.budgetProposalName
                }`,
        },
        {
            field: 'phase',
            headerName: t('common.phase'),
            maxWidth: 250,
            cellRenderer: 'phaseCellRenderer',

            valueGetter: ({
                data,
            }: {
                data: { budgetProposalPhase: string };
            }) => data.budgetProposalPhase,
        },
    ];

    return columns;
};
